/**
 * B2B の利用開始前の表示（申請・ログイン・確認コード・招待・受付完了）。
 * 認証・検証・申請先を持たず、各面の既存の処理に共通の枠と入力部品を提供する。
 */
import { TRAINER_LP_URL } from "../coaches.js";
import { B2B_APPLICATION_LIMITS } from "../applications/index.js";
import { activeB2BApplyOptions } from "../applications/client.js";
import { logoSvg } from "@kalori/brand/logo";
import { B2B_ONBOARDING_STYLE } from "./styles.js";
export { B2B_ONBOARDING_STYLE } from "./styles.js";
/** カード左上のロゴの高さ（px）。merchant の React 版（`ui/src/screens/Onboarding.tsx`）も同じ値で描く。 */
export const B2B_BRAND_LOGO_HEIGHT = 18;
/**
 * 「ロゴ + for Coaches」の中身（SSR の各面のヘッダー・カード共通）。社名は文字で組まずロゴで出す。
 * 読み上げは「Kalori」+「for Coaches」で製品名になる。並べ方（flex・baseline）は置き場所の CSS が持つ。
 */
export function b2bBrandMark(line, height = B2B_BRAND_LOGO_HEIGHT) {
    return `${logoSvg({ height, label: "Kalori" })}<span>${B2B_ONBOARDING_PRODUCTS[line].suffix}</span>`;
}
export const B2B_ONBOARDING_PRODUCTS = {
    trainer: { suffix: "for Coaches", name: "Kalori for Coaches", lpUrl: TRAINER_LP_URL },
    developer: { suffix: "for Developers", name: "Kalori for Developers", lpUrl: "https://kalori.jp/ja/business/catalog-api/" },
    merchant: { suffix: "for Brands", name: "Kalori for Brands", lpUrl: "https://kalori.jp/ja/business/brands/" },
};
export function onboardingFooterLinks(line) {
    return [
        { href: B2B_ONBOARDING_PRODUCTS[line].lpUrl, label: "サービスのご案内" },
        ...(line === "developer" ? [{ href: "/docs", label: "ドキュメント" }] : []),
        { href: "https://kalori.jp/ja/privacy/", label: "プライバシーポリシー" },
        { href: "mailto:contact@kalori.jp", label: "お問い合わせ" },
    ];
}
function escapeHtml(value) {
    return String(value ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]);
}
/** body は呼び出し元が組み立てる HTML。利用者由来の値は部品側、または呼び出し元で必ずエスケープする。 */
export function renderB2BOnboarding(line, title, body) {
    const product = B2B_ONBOARDING_PRODUCTS[line];
    const footer = onboardingFooterLinks(line).map((link) => `<a href="${escapeHtml(link.href)}">${escapeHtml(link.label)}</a>`).join("");
    return `<!DOCTYPE html><html lang="ja"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1"><meta name="robots" content="noindex"><title>${escapeHtml(title)} | ${product.name}</title><style>body{margin:0}${B2B_ONBOARDING_STYLE}</style></head>
<body><main class="b2b-onboarding"><section class="b2b-card"><a class="b2b-brand" href="${product.lpUrl}">${b2bBrandMark(line)}</a><h1>${escapeHtml(title)}</h1>${body}</section><footer class="b2b-footer" aria-label="サポート">${footer}<span>© kalbi合同会社</span></footer></main></body></html>`;
}
export function onboardingFieldLabel(label, required = false) {
    return !required && !label.includes("任意") ? `${label}（任意）` : label;
}
function fieldLabel(label, required = false) {
    return escapeHtml(onboardingFieldLabel(label, required)) + (required ? '<span class="b2b-required">必須</span>' : "");
}
export function renderOnboardingField(field) {
    const id = `b2b-${field.name}`;
    const hintId = `${id}-hint`;
    const attrs = `id="${escapeHtml(id)}" name="${escapeHtml(field.name)}"${field.required ? " required" : ""}${field.maxLength ? ` maxlength="${field.maxLength}"` : ""}${field.autocomplete ? ` autocomplete="${escapeHtml(field.autocomplete)}"` : ""}${field.inputmode ? ` inputmode="${field.inputmode}"` : ""}${field.pattern ? ` pattern="${escapeHtml(field.pattern)}"` : ""}${field.placeholder ? ` placeholder="${escapeHtml(field.placeholder)}"` : ""}${field.hint ? ` aria-describedby="${escapeHtml(hintId)}"` : ""}${field.autofocus ? " autofocus" : ""}${field.code ? ' class="b2b-code"' : ""}`;
    const input = field.type === "textarea" ? `<textarea ${attrs}>${escapeHtml(field.value)}</textarea>` : `<input type="${field.type ?? "text"}" ${attrs} value="${escapeHtml(field.value)}">`;
    return `<div class="b2b-field"><label class="b2b-label" for="${escapeHtml(id)}">${fieldLabel(field.label, field.required)}</label>${input}${field.hint ? `<p class="b2b-hint b2b-hint-after" id="${escapeHtml(hintId)}">${escapeHtml(field.hint)}</p>` : ""}</div>`;
}
/** 長い選択肢は縦に並べ、短い選択肢は折り返す。同じ設問定義を SSR と React が読む。 */
export function onboardingChoicesClass(q) {
    return "b2b-choices" + (activeB2BApplyOptions(q.options ?? []).some((o) => o.label.length > 14) ? " b2b-choices-long" : "");
}
export function renderOnboardingQuestion(q, value = "") {
    if (!q.options)
        return renderOnboardingField({ name: q.key, label: q.label, value, required: q.required, maxLength: B2B_APPLICATION_LIMITS.attrValue, placeholder: q.placeholder, hint: q.hint });
    const hintId = `b2b-${q.key}-hint`;
    const options = activeB2BApplyOptions(q.options).map((o) => `<label class="b2b-choice"><input type="radio" name="${escapeHtml(q.key)}" value="${escapeHtml(o.value)}"${q.required ? " required" : ""}${value === o.value ? " checked" : ""}><span>${escapeHtml(o.label)}</span></label>`).join("");
    return `<fieldset class="b2b-field"${q.hint ? ` aria-describedby="${escapeHtml(hintId)}"` : ""}><legend class="b2b-label">${fieldLabel(q.label, q.required)}</legend>${q.hint ? `<p class="b2b-hint" id="${escapeHtml(hintId)}">${escapeHtml(q.hint)}</p>` : ""}<div class="${onboardingChoicesClass(q)}">${options}</div></fieldset>`;
}
export function renderOnboardingError(error) {
    return error ? `<p class="b2b-error" role="alert">${escapeHtml(error)}</p>` : "";
}
//# sourceMappingURL=index.js.map